import React from 'react';
import {Tabs} from '@mantine/core';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {TrainingDashViewType} from 'src/shared/constants/ViewTypes';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export type TrainingLandingTabView =
  | TrainingDashViewType
  | 'compliance'
  | 'schedule'
  | 'sign-offs';

export interface TrainingLandingTabsProps {
  active: TrainingLandingTabView;
}

/**
 * Top-level Training tab bar shared by the Training dashboard views
 * (My Training / Courses / Media) and the standalone Compliance, Schedule
 * and Sign-offs pages, so the tabs remain visible and navigable on every
 * top-level Training destination.
 */
export const TrainingLandingTabs: React.FC<TrainingLandingTabsProps> = ({
  active,
}) => {
  const navigate = useAppNavigate();
  const trainingPerms = useModulePermissions(ModuleEnum.Training);

  const handleChange = (value: string | null) => {
    if (!value || value === active) return;
    switch (value as TrainingLandingTabView) {
      case 'compliance':
        navigate(PathUtility.TrainingCompliance());
        break;
      case 'schedule':
        navigate(PathUtility.TrainingSchedule());
        break;
      case 'sign-offs':
        navigate(PathUtility.TrainingSignOffs());
        break;
      default:
        navigate(PathUtility.TrainingDashboard(value as TrainingDashViewType));
    }
  };

  return (
    <Tabs value={active} onChange={handleChange}>
      <Tabs.List mb="md">
        <Tabs.Tab value="user">My Training</Tabs.Tab>
        <Tabs.Tab value="manage" disabled={!trainingPerms.view}>
          Courses
        </Tabs.Tab>
        <Tabs.Tab value="course-video" disabled={!trainingPerms.view}>
          Media
        </Tabs.Tab>
        <Tabs.Tab value="compliance" disabled={!trainingPerms.view}>
          Compliance
        </Tabs.Tab>
        <Tabs.Tab value="schedule" disabled={!trainingPerms.view}>
          Schedule
        </Tabs.Tab>
        <Tabs.Tab value="sign-offs" disabled={!trainingPerms.view}>
          Sign-offs
        </Tabs.Tab>
      </Tabs.List>
    </Tabs>
  );
};
